import { z } from 'zod';
import { AYLAR, SABIT_GIDER_ALANLARI } from '../kar-tablosu/hesap.js';

/**
 * §KT — Kâr tablosu girdi doğrulaması.
 *
 * Tutarlar kullanıcının elle girdiği rakamlar; tabloya yanlış bir sayı
 * girilirse o ayın kârı bozulur. Bu yüzden alanlar sınırlı: eksi tutar
 * kabul edilmiyor, tarih gerçek bir tarih olmak zorunda.
 */

const ay = z.enum(AYLAR as unknown as [string, ...string[]]);
const yil = z.string().regex(/^\d{4}$/, 'Yıl dört haneli olmalı');

/** Para alanı — eksi olamaz, tek satır bu sınırın üstüne çıkmaz. */
const tutar = z.number().min(0).max(1_000_000);

export const karSatirSchema = z.object({
  ulke: z.string().min(1, 'Ülke seçin').max(40),
  tarih: z
    .string()
    .min(1, 'Tarih girin')
    .refine((v) => !Number.isNaN(Date.parse(v)), 'Geçerli bir tarih girin'),
  asin: z.string().max(20).nullish(),
  orderNo: z.string().max(60).nullish(),
  buyerNo: z.string().max(60).nullish(),
  gelir: tutar,
  vat: tutar,
  amazonFee: tutar,
  promosyon: tutar,
  buyFee: tutar,
  depoFee: tutar,
  iadeFee: tutar,
  otherFee: tutar,
  notAciklama: z.string().max(500).nullish(),
});
export type KarSatirDto = z.infer<typeof karSatirSchema>;

/** Oranlar yüzde olarak giriliyor; %100'ün üstü kâr hesabını anlamsız kılar. */
const oran = z.number().min(0).max(100);

export const karOranSchema = z.object({
  /** Boşsa mağaza geneli oran kaydediliyor, doluysa yalnızca o ay. */
  year: yil.nullish(),
  month: ay.nullish(),
  kkRate: oran,
  pCekmeRate: oran,
  amzCeviriRate: oran,
  kurCeviriRate: oran,
  faizRate: oran,
});
export type KarOranDto = z.infer<typeof karOranSchema>;

export const sabitGiderSchema = z.object(
  Object.fromEntries(SABIT_GIDER_ALANLARI.map((a) => [a, tutar])) as Record<
    (typeof SABIT_GIDER_ALANLARI)[number],
    typeof tutar
  >,
);
export type SabitGiderDto = z.infer<typeof sabitGiderSchema>;

export const karMailAyarSchema = z
  .object({
    saticiEmail: z.string().email('Geçerli bir e-posta adresi girin'),
    /** Boş bırakılırsa kayıtlı parola korunuyor. */
    saticiParola: z.string().max(200).optional(),
    aliciEmail: z.string().email('Geçerli bir e-posta adresi girin').or(z.literal('')).optional(),
    aliciParola: z.string().max(200).optional(),
  })
  .strict();
export type KarMailAyarDto = z.infer<typeof karMailAyarSchema>;

export const karMailCekSchema = z
  .object({
    siparisNo: z.string().max(60).default(''),
    aliciNo: z.string().max(60).default(''),
  })
  .refine((v) => v.siparisNo.trim().length > 0 || v.aliciNo.trim().length > 0, {
    message: 'Sipariş numarası ya da alıcı numarası girin',
    path: ['siparisNo'],
  });
export type KarMailCekDto = z.infer<typeof karMailCekSchema>;
